import React from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import useHasResume from '../hooks/useHasResume';
import '../claudeTheme.css';
import './RequireResume.css';

// Nothing works without a resume.
//
// ── Why a screen and not a redirect ────────────────────────────────────────
//
// Bouncing somebody to the Design Studio answers "where do I go" and throws
// away "why am I here" — they arrive in an editor with no idea what happened
// to the page they asked for. This keeps the URL, says what is missing in one
// sentence, and offers the two ways out. When they come back, the page they
// wanted is still the page they are on.
//
// ── What is NOT behind this ────────────────────────────────────────────────
//
// The Design Studio, because it is where a resume gets built, and Account,
// because it is where one gets uploaded and where sign-out and billing live.
// Gating either would leave somebody with no way to satisfy the gate — a lock
// with the key inside it.

const RequireResume = ({ children }) => {
  const { loading, hasResume } = useHasResume();
  const navigate = useNavigate();
  const location = useLocation();

  // A spinner while the check is in flight — not the "you need a resume"
  // card, which would read as a verdict, and not nothing, which on a slow
  // server was a blank content pane for up to two request round trips.
  if (loading) {
    return (
      <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', width: '100%' }} role="status">
        <Spinner animation="border" variant="primary"><span className="visually-hidden">Loading…</span></Spinner>
      </div>
    );
  }
  if (hasResume) return children;

  return (
    <div className="claude-theme rq-ground">
      <div className="rq-card surface-panel">
        <h1 className="rq-title">First, a resume</h1>
        <p className="rq-body">
          Everything here works from your resume — matching jobs to you, scoring
          how well you fit, and filling in applications. Add one and the rest of
          the site opens up.
        </p>

        <div className="rq-actions">
          <button
            className="rq-btn rq-btn-primary"
            onClick={() => navigate('/account')}
          >
            {/* "resume", like the title, the body, the button this leads to
                in Account and the signup screen. It said "CV" here alone, so
                the same journey used two words for one thing. */}
            Upload a resume
          </button>
          <button
            className="rq-btn"
            onClick={() => navigate('/design-studio')}
          >
            Build one instead
          </button>
        </div>

        <p className="rq-foot">
          {/* Named, because "you were trying to reach a page" is vaguer than
              the person already knows. */}
          You were heading to <code>{location.pathname}</code>. It will be here
          when you get back.
        </p>
      </div>
    </div>
  );
};

export default RequireResume;
